import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  AccessRole,
  AccessUser,
  Department,
  Organization,
  createUser,
  fetchUsers,
  updateUser,
} from '../../../api/accessApi';
import { formatTime, useAsync } from './shared';

interface UsersTabProps {
  roles: AccessRole[];
  orgs: Organization[];
  departments: Department[];
  myLevel: number;
}

export default function UsersTab({ roles, orgs, departments, myLevel }: UsersTabProps) {
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [showForm, setShowForm] = useState(false);
  const { error, busy, run } = useAsync();
  const [form, setForm] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role_id: '',
    organization_id: '',
    department_id: '',
  });

  const load = useCallback(() => run(fetchUsers).then((u) => u && setUsers(u)), [run]);
  useEffect(() => {
    load();
  }, [load]);

  // Roles must be strictly below the viewer's own level; the server enforces this too.
  const assignable = roles.filter((r) => r.level < myLevel);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const created = await run(() =>
      createUser({
        username: form.username.trim(),
        password: form.password,
        full_name: form.full_name.trim(),
        email: form.email.trim() || undefined,
        role_id: Number(form.role_id),
        organization_id: form.organization_id ? Number(form.organization_id) : null,
        department_id: form.department_id ? Number(form.department_id) : null,
      }),
    );
    if (created) {
      setForm({ username: '', password: '', full_name: '', email: '', role_id: '', organization_id: '', department_id: '' });
      setShowForm(false);
      load();
    }
  };

  const change = async (id: number, body: Parameters<typeof updateUser>[1]) => {
    const updated = await run(() => updateUser(id, body));
    if (updated) setUsers((prev) => prev.map((u) => (u.id === id ? updated : u)));
  };

  const resetPassword = async (u: AccessUser) => {
    const password = window.prompt(`New password for ${u.username} (min 8 characters):`);
    if (password) await change(u.id, { password });
  };

  return (
    <section className="panel">
      <div className="acc-bar">
        <h2>Users</h2>
        <button className="btn primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'Add user'}
        </button>
      </div>
      {error && <p className="acc-error">{error}</p>}
      {showForm && (
        <form className="acc-form" onSubmit={submit}>
          <label>
            Username
            <input required minLength={3} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          </label>
          <label>
            Password
            <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </label>
          <label>
            Full name
            <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          </label>
          <label>
            Email
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <label>
            Role
            <select required value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })}>
              <option value="">Select a role</option>
              {assignable.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Organization
            <select value={form.organization_id} onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
              <option value="">None (internal)</option>
              {orgs.filter((o) => o.is_active).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Department
            <select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
              <option value="">None</option>
              {departments.filter((d) => d.is_active).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <button className="btn primary" disabled={busy}>
            Create user
          </button>
        </form>
      )}
      <div className="acc-scroll">
        <table className="acc-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Organization</th>
              <th>Department</th>
              <th>Last login</th>
              <th>Tools used today</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.is_active ? '' : 'acc-off'}>
                <td>
                  <b>{u.username}</b>
                  <span>{u.full_name || u.email || ''}</span>
                </td>
                <td>
                  <select
                    value={u.role_id ?? ''}
                    disabled={busy}
                    onChange={(e) => change(u.id, { role_id: Number(e.target.value) })}
                  >
                    {u.role_id === null && <option value="">No role</option>}
                    {roles
                      .filter((r) => r.id === u.role_id || r.level < myLevel)
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                  </select>
                </td>
                <td>
                  <select
                    value={u.organization_id ?? ''}
                    disabled={busy}
                    onChange={(e) =>
                      change(u.id, { organization_id: e.target.value ? Number(e.target.value) : null })
                    }
                  >
                    <option value="">None</option>
                    {orgs.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={u.department_id ?? ''}
                    disabled={busy}
                    onChange={(e) =>
                      change(u.id, { department_id: e.target.value ? Number(e.target.value) : null })
                    }
                  >
                    <option value="">None</option>
                    {departments
                      .filter((d) => d.is_active || d.id === u.department_id)
                      .map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                  </select>
                </td>
                <td>{formatTime(u.last_login_at)}</td>
                <td>
                  {u.usage_today.length === 0
                    ? '—'
                    : u.usage_today.map((t) => (
                        <span key={t.tool_id} style={{ display: 'block' }}>
                          {t.tool} × {t.count}
                        </span>
                      ))}
                </td>
                <td>{u.is_active ? 'Active' : 'Inactive'}</td>
                <td className="acc-actions">
                  <button className="btn" disabled={busy} onClick={() => resetPassword(u)}>
                    Reset password
                  </button>
                  <button className="btn" disabled={busy} onClick={() => change(u.id, { is_active: !u.is_active })}>
                    {u.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && !busy && <p className="note">No users to show.</p>}
      </div>
    </section>
  );
}
